import type { CurrentSessionUser, UpdateMeCoache } from '@entities/Authentication/types.ts';
import { useContacts } from '@entities/contacts/index.ts';
import { yupResolver } from '@hookform/resolvers/yup';
import { forwardRef, useCallback, useEffect, useMemo, type Ref } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useUpdateMe } from '@pages/personal/model.ts';
import { coachVideoApi } from '@widgets/personal/editor-personal-coach/model/api.ts';
import { useCoacheEditorSavedData } from '@widgets/personal/editor-personal-coach/model/model.ts';
import { EditorPartitions } from '@widgets/programs/index.ts';
import { FormContactsEditor } from '@widgets/shared/Forms/ContactsEditor/index.ts';
import { FormDescriptionEditor } from '@widgets/shared/Forms/DescriptionEditor/index.tsx';
import { useAppDispatch, usePopup, useUser } from '@shared/hooks/index.ts';
import { usePartitionsWithTagsSelection } from '@shared/hooks/use-partitions-with-tags-selection.ts';
import { useVideoLoader } from '@shared/hooks/useVideoLoader.ts';
import * as shared from '@shared/styles/sharedStyles.module.scss';
import { Card, TextInputM, UploadProgressCard } from '@shared/ui';
import { FormVideo } from '@shared/ui/forms/video/index.ts';
import { ImageInputCropper } from '@shared/ui/inputs/image-input-cropper';
import { EditCoachProfile } from '@shared/yupValidators/validators.ts';
import { Loader } from '@src/library/components/common/index.ts';
import * as st from './styles.module.scss';

const getParsedUser = ({ user }: { user?: CurrentSessionUser }) => {
  if (!user) {
    return undefined;
  } else {
    return {
      photo: user?.photo || null,
      first_name: user?.first_name,
      middle_name: user?.middle_name,
      last_name: user?.last_name,
      active_contacts: user?.active_contacts?.map((c) => c.id) || [],
      description: user?.description,
      tags: user?.tags?.map((s) => s.id) || [],
      url: user?.coach_video?.embed_url || user?.coach_video?.metadata?.FileName,
      video_type: user?.coach_video?.video_type,
      partitions: user?.partitions?.map((p) => p.id) || [],
    };
  }
};

export const EditorPersonalCoach = forwardRef<
  HTMLFormElement,
  {
    hash: string;
    setIsShouldCloseModalOpen: (val: boolean) => void;
  }
>(({ hash, setIsShouldCloseModalOpen }, ref: Ref<HTMLFormElement>) => {
  const { user } = useUser();
  const { selectedContacts } = useContacts();

  const { updateMe, isLoading: isUpdatingMeLoading } = useUpdateMe();
  const navigate = useNavigate();

  const { progress, uploadStatus, create } = useVideoLoader({
    api: coachVideoApi,
  });
  const dispatch = useAppDispatch();
  const { popup } = usePopup();
  const isUploading = uploadStatus === 'loading';

  const { savedData, doSetStorageData, doRemoveStorageData } = useCoacheEditorSavedData();

  const { clearPartitionsData, selectedPartitions, selectedTags } = usePartitionsWithTagsSelection({
    hash: hash,
    initializeData: {
      tags: user?.tags || [],
      partitions: user?.partitions || [],
      savedFormState: savedData,
    },
  });

  const parsedUser = useMemo(() => getParsedUser({ user }), [user]);

  const formValues = savedData
    ? {
        ...savedData,
        tags: selectedTags,
        partitions: selectedPartitions,
        active_contacts: selectedContacts,
      }
    : parsedUser;

  const formContext = useForm<UpdateMeCoache>({
    resolver: yupResolver(EditCoachProfile) as any,
    defaultValues: {
      tags: selectedTags,
      partitions: selectedPartitions,
    },
    values: formValues,
  });

  const {
    setValue,
    setError,
    register,
    getValues,
    handleSubmit,
    formState: { errors, isDirty },
  } = formContext;
  const onRedirect = useCallback(
    () =>
      doSetStorageData({
        ...getValues(),
        active_contacts: selectedContacts,
      }),
    [doSetStorageData, getValues, selectedContacts]
  );

  const onSubmit = async (data: UpdateMeCoache) => {
    let videoId;
    const { isNewVideo, file, url, video_type, isVideoDeleted, photo, ...restData } = data;
    if (isVideoDeleted) {
      videoId = null;
    }
    if (isNewVideo) {
      videoId = await create({
        data: { file: file, url: url, video_type: video_type },
      });
    }
    await updateMe({
      data: {
        ...restData,
        photo: photo || null,
        module: restData.module === 0 ? null : restData.module,
        id: String(user?.id),
        coach_video: videoId,
      },
      onSuccess: () => {
        navigate('/personal');
        setTimeout(() => {
          doRemoveStorageData();
          clearPartitionsData();
        }, 0);
        dispatch(popup({ type: 'success', text: `Изменения успешно сохранены` }));
      },
      onError: ({ message, fieldName }) => {
        setError(fieldName as keyof UpdateMeCoache, { type: 'value', message });
      },
    });
  };

  useEffect(() => {
    setIsShouldCloseModalOpen(Boolean(savedData || isDirty));
  }, [setIsShouldCloseModalOpen, savedData, isDirty]);

  return isUploading ? (
    <UploadProgressCard
      progress={progress}
      title="Данные обновляются"
      customStyles={{
        position: 'absolute',
        top: '0',
        padding: '114px',
        justifyContent: 'flex-start',
      }}
    />
  ) : (
    <form className={shared.forFormsGap20} onSubmit={handleSubmit(onSubmit)} ref={ref}>
      {isUpdatingMeLoading ? (
        <Loader />
      ) : (
        <>
          <Card className={st.photoFio}>
            <div className={st.photo}>
              <ImageInputCropper
                aspect={[1, 1]}
                iconVariant="person"
                value={formValues?.photo || ''}
                onChange={(value) => {
                  setValue(`photo`, value);
                }}
                isValid={!!errors['photo']}
              />
            </div>

            <div className={st.fio}>
              <TextInputM register={register('last_name')} fullWidth placeholder="Фамилия" />
              <TextInputM register={register('first_name')} fullWidth placeholder="Имя" />
              <TextInputM register={register('middle_name')} fullWidth placeholder="Отчество" />
            </div>
          </Card>

          <EditorPartitions
            onRedirect={onRedirect}
            hash={hash}
            label={'СПЕЦИАЛИЗАЦИЯ'}
            code={user?.module?.code}
            tagsPageTitle={'Выбор специализации / теги'}
            partitionsPageTitle={'Специализации'}
          />

          <FormDescriptionEditor
            fieldLabel="Обо мне"
            hash={hash}
            onChange={(v) => setValue('description', v, { shouldDirty: v !== '' })}
            onRedirect={onRedirect}
            initData={user?.description}
          />

          <FormVideo
            isDisabledSelectionFromLibrary={true}
            formContextFromProps={formContext}
            isEditMode
            fieldLabel={'ПРЕЗЕНТАЦИОННОЕ ВИДЕО'}
          />

          <FormContactsEditor
            initData={formValues?.active_contacts}
            onRedirect={onRedirect}
            onChange={(newContacts) => setValue('active_contacts', newContacts)}
          />
        </>
      )}
    </form>
  );
});
